import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { from, map, switchMap } from 'rxjs';
import { getAggregatedFieldStages } from '../aggregated-field.js';
import { AggregatedEntity } from '../entities/aggregated-entity.js';

export function getAggregatedEntityAggregation<Tfilter extends AdornisFilter>(
  entity: typeof AggregatedEntity,
  filter: Tfilter,
  gqlFields: BaseQLSelectionSet,
) {
  const baseCollectionName = entity.baseCollectionName;
  if (!baseCollectionName) throw new Error('Base collection name not found');
  if (baseCollectionName === '_empty') throw new Error('baseCollectionName has to be overriden for: ' + entity._class);
  const aggregationStages = entity.getAggregation(filter, gqlFields);

  // Add lookup stages from AggregatedField decorators
  const fieldAggregations = [] as any[];
  const aggregatedFieldStages = getAggregatedFieldStages(entity);
  if (aggregatedFieldStages.length > 0) {
    // Add the lookup stages to the aggregation pipeline
    for (const stage of aggregatedFieldStages) {
      const lookupStage = (stage as any).lookupStage(entity, gqlFields);
      if (lookupStage.$lookup === null) continue;
      fieldAggregations.push(lookupStage);
      // unpack result if field is not an array
      if (!stage.isArray) fieldAggregations.push({ $set: { [stage.fieldName]: { $first: `$${stage.fieldName}` } } });
    }
  }

  return [
    ...aggregationStages.entityDataAggregation,
    ...fieldAggregations,
    ...aggregationStages.postFieldResolverAggregation,
  ];
}

function getAggregatedEntityUpdateNotificationStream(entity: typeof AggregatedEntity, filter: AdornisFilter) {
  // Watch the base collection
  const baseCollectionStream = watchEntireCollection(entity.baseCollectionName);

  // Also watch collections from AggregatedField decorators
  const aggregatedFieldStages = getAggregatedFieldStages(entity._class as any);
  if (aggregatedFieldStages.length === 0) {
    return baseCollectionStream;
  }

  // We could also watch the collections referenced by the aggregated fields
  // and merge those streams, but for now we'll just watch the base collection
  return baseCollectionStream;
}

function subscribeAllAggregatedEntitiesServer<Tfilter extends AdornisFilter>(
  entityClass: string,
  skip: number,
  limit: number,
  filter: Tfilter,
  gqlFields: BaseQLSelectionSet,
) {
  return from(
    (async () => {
      const node = baseqlMetaData.entities.find(entity => entity._class === entityClass);
      if (!node) throw new Error('Entity node not found');
      const entity = node.type() as Maybe<typeof AggregatedEntity>;
      if (!entity) throw new Error('Entity not found');
      const aggregation = getAggregatedEntityAggregation(entity, filter, gqlFields);

      // add skip and limit
      // aggregation.push({ $project: gqlFields });
      aggregation.push({ $skip: skip });
      aggregation.push({ $limit: limit });

      return { entity, aggregation };
    })(),
  ).pipe(
    switchMap(async ({ entity, aggregation }) => {
      const collection = mongo().collection(entity.baseCollectionName);
      const updateNotificationStream = getAggregatedEntityUpdateNotificationStream(entity, filter);
      return { entity, aggregation, collection, updateNotificationStream };
    }),
    switchMap(({ entity, aggregation, collection, updateNotificationStream }) => {
      return updateNotificationStream.pipe(map(() => ({ entity, collection, aggregation })));
    }),
    switchMap(async ({ entity, collection, aggregation }) => {
      const result = await collection.aggregate(aggregation).toArray();

      // check if all items have _class to make debugging easier
      for (const item of result) {
        if (!item._id)
          throw new Error(`At least on item of the ${entity._class}->getAggregation result has no _id field`);
        if (!item._class)
          throw new Error(`At least on item of the ${entity._class}->getAggregation result has no _class field`);
      }

      // logger.error({ result, gqlFields }, 'subscribeAllAggregatedEntities');

      // throw 'stop it';

      return result;
    }),
  );
}

const aggregatedEntitySubscriptionsRegistry = {} as any;
export function registerAggregatedEntityApiMethods() {
  baseqlMetaData.entities.forEach(entity => {
    const isAggregatedEntity = (() => {
      let parent = entity.parent;
      while (parent) {
        if (parent._class === AggregatedEntity._class) {
          return true;
        }
        parent = parent.parent;
      }
      return false;
    })();

    if (isAggregatedEntity) {
      const filterType = (() => {
        const _entity = entity.type();
        try {
          console.log('stuff:', Object.keys(_entity));
          const filterType = (_entity as typeof AggregatedEntity).getFilter();
          console.log('got it', filterType);
          return filterType;
        } catch (e) {
          return AdornisFilter;
        }
      })();

      aggregatedEntitySubscriptionsRegistry[entity._class] = registerSubscription({
        operationName: 'subscribeAllAggregatedEntities_' + entity._class,
        type: () => [AggregatedEntity],
        middlewares: [checkAuthorizedMiddleware(({ user }) => user.isSuperAdmin)],
        params: [
          { name: 'entityClass', type: () => String },
          { name: 'skip', type: () => Int },
          { name: 'limit', type: () => Int },
          { name: 'filter', type: () => filterType },
        ],
        resolve:
          (entityClass: string, skip: number, limit: number, filter: any) =>
          (gqlFields: BaseQLSelectionSet<AggregatedEntity>) => {
            return subscribeAllAggregatedEntitiesServer(entityClass, skip, limit, filter, gqlFields);
          },
      });
    }
  });
}

export const subscribeAllAggregatedEntities = function (
  entityClass: string,
  skip: number,
  limit: number,
  filter: AdornisFilter,
) {
  console.log({ entityClass, skip, limit, filter });
  const fn = aggregatedEntitySubscriptionsRegistry[entityClass];
  console.log({ fn });
  return fn(entityClass, skip, limit, filter);
};
